import { useCallback, useEffect, useState } from 'react'
import { getMemorySettings, updateMemorySettings } from '../services/memorySettingsApi'
import type { MemorySettings, MemorySettingsField } from '../types'

type FieldState = Partial<Record<MemorySettingsField, boolean>>
type FieldErrors = Partial<Record<MemorySettingsField, string>>

// `enabled` lets a caller skip the fetch entirely (e.g. a workspace member,
// who is forbidden from GET .../memory-settings) without violating the rules
// of hooks by conditionally calling this hook.
export function useMemorySettings(workspaceId: string, enabled = true) {
  const [settings, setSettings] = useState<MemorySettings | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)
  const [savingFields, setSavingFields] = useState<FieldState>({})
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const load = useCallback(() => {
    if (!enabled) {
      setLoading(false)
      return () => {}
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    getMemorySettings(workspaceId)
      .then((s) => { if (!cancelled) setSettings(s) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load memory settings') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [workspaceId, enabled])

  useEffect(() => load(), [load])

  // Each toggle saves immediately: flip it, fire the PUT, reconcile with the
  // server response on success or revert + surface an inline error on failure.
  // No page-level Save button for this tab.
  const toggleTier = useCallback(async (field: MemorySettingsField) => {
    setSettings((prev) => {
      if (!prev) return prev
      const previousValue = prev[field]
      const next = { ...prev, [field]: !previousValue }

      setFieldErrors((e) => ({ ...e, [field]: undefined }))
      setSavingFields((s) => ({ ...s, [field]: true }))

      updateMemorySettings(workspaceId, { [field]: !previousValue })
        .then((resolved) => {
          setSettings(resolved)
        })
        .catch((err) => {
          setSettings((cur) => (cur ? { ...cur, [field]: previousValue } : cur))
          setFieldErrors((e) => ({
            ...e,
            [field]: err instanceof Error ? err.message : 'Failed to save',
          }))
        })
        .finally(() => {
          setSavingFields((s) => ({ ...s, [field]: false }))
        })

      return next
    })
  }, [workspaceId])

  return { settings, loading, error, retry: load, savingFields, fieldErrors, toggleTier }
}
